import React, { memo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Print from '@mui/icons-material/PrintOutlined';
import RequestQuote from '@mui/icons-material/RequestQuoteOutlined';
import type { SxProps, Theme } from '@mui/material/styles';
import type { SubmitStatus } from './hooks/useLabelJobSubmit';
import { LabelFormat, plural } from './labelForm';

const barSx: SxProps<Theme> = {
  position: 'sticky',
  bottom: 16,
  zIndex: 4,
  display: 'flex',
  alignItems: 'center',
  gap: 2,
  flexWrap: 'wrap',
  py: 1.5,
  pr: 1.5,
  pl: 2.5,
  mt: 1,
  bgcolor: 'background.paper',
  border: 1,
  borderColor: 'divider',
  borderRadius: 4,
  boxShadow: '0 6px 24px rgba(0,21,41,.10)',
};

const hintSx: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: 1, fontSize: 13, minWidth: 0, '& svg': { fontSize: 18 } };

interface LabelActionBarProps {
  status: SubmitStatus;
  missing: readonly string[];
  labelFormats: readonly LabelFormat[];
  packageCount: number;
  testMode: boolean;
  generateLabels: boolean;
}

const describeMissing = (missing: readonly string[]) =>
  `Missing ${missing.slice(0, 2).join(', ')}${missing.length > 2 ? ` +${missing.length - 2}` : ''}`;

/** Sticky footer: validation hint plus the submit button for the enclosing form. */
const LabelActionBar: React.FC<LabelActionBarProps> = ({ status, missing, labelFormats, packageCount, testMode, generateLabels }) => {
  const submitting = status.phase === 'submitting';
  const blocked = missing.length > 0 || submitting;
  const production = !testMode;

  let message: React.ReactNode;
  if (missing.length) {
    message = (
      <Box sx={[hintSx, { color: 'error.main' }] as SxProps<Theme>}>
        <ErrorOutline />
        {describeMissing(missing)}
      </Box>
    );
  } else {
    const output = generateLabels ? labelFormats.map((f) => f.toUpperCase()).join(' + ') : 'Rate shop';
    message = (
      <Box sx={[hintSx, { color: 'text.secondary' }] as SxProps<Theme>}>
        <InfoOutlined />
        {`${output} · ${plural(packageCount, 'package')} · ${production ? 'Production' : 'Test mode'}`}
      </Box>
    );
  }

  return (
    <Box sx={barSx}>
      {message}
      <Button
        type="submit"
        variant="contained"
        disabled={blocked}
        startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : generateLabels ? <Print /> : <RequestQuote />}
        sx={{
          ml: 'auto',
          height: 48,
          px: 3,
          pl: 2.5,
          borderRadius: 6,
          fontSize: 15,
          fontWeight: 600,
          boxShadow: '0 1px 2px rgba(0,21,41,.15)',
          ...(production && { bgcolor: 'warning.main', '&:hover': { bgcolor: 'warning.dark' } }),
        }}
      >
        {submitting ? 'Creating job…' : generateLabels ? `Generate ${testMode ? 'test ' : ''}labels` : 'Get rates'}
      </Button>
    </Box>
  );
};

export default memo(LabelActionBar);
